import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Sparkles } from 'lucide-react';
import { assetToast } from '@xdocs/i18n';
import { errorAlert } from '@xdocs/schemas';
import type { JobAccepted } from '@xdocs/schemas';
import { requestTyped } from '@xdocs/ws-protocol';
import { Badge, ListRow, SegmentedControl, Textarea, cn, useI18n, useToast } from '@xdocs/ui';
import { useJobSnapshot } from '../jobs/index.js';
import { useWs } from '../../ws/WsProvider.js';
import { EnhancementPanelShell } from './EnhancementPanelShell.js';
import type { EnhancementPanelPhase } from './EnhancementPanelShell.js';
import { useActionEstimate } from './useActionEstimate.js';
import { useEnhancementT } from './useEnhancementT.js';
import styles from './ExpandPanel.module.css';

export interface ExpandPanelProps {
  documentId: string;
  /** Required — names the asset in the done/error toasts (§16.1). */
  documentTitle: string;
  versionId: string;
  className?: string | undefined;
}

const HOW_MUCH_TEXT: Record<'few' | 'untilTopic', string> = {
  few: 'Add a few more chapters (about 3).',
  untilTopic: 'Continue until the current topic is fully covered.',
};

/**
 * F2 — unlimited expandable documents (PRO). Self-contained trigger + panel:
 * mount `<ExpandPanel .../>` anywhere in the editor actions rail — no
 * external wiring beyond the three id props. Estimate → confirm(+cost) →
 * job progress → done, with the F48 AC "PRO-locked tier is a lock, not a
 * dead control" upsell state firing off a `TIER_REQUIRED` reply.
 */
export function ExpandPanel({ documentId, documentTitle, versionId, className }: ExpandPanelProps): ReactElement {
  const { t } = useEnhancementT();
  const { locale } = useI18n();
  const { push: pushToast } = useToast();
  const { wsClient } = useWs();

  const [open, setOpen] = useState(false);
  // Only ever 'form' | 'starting' | 'upsell' | 'running' — the terminal
  // 'done'/'error' phases are DERIVED from the live job snapshot below
  // (react-hooks purity: never setState synchronously inside an effect).
  const [phase, setPhase] = useState<EnhancementPanelPhase>('form');
  const [howMuch, setHowMuch] = useState<'few' | 'untilTopic'>('few');
  const [direction, setDirection] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { estimate } = useActionEstimate('expand.estimate', open ? { documentId, versionId } : null);
  const { snapshot } = useJobSnapshot(jobId);

  const jobSucceeded = phase === 'running' && snapshot?.job.status === 'succeeded';
  const jobFailedTerminal =
    phase === 'running' && snapshot?.job.status === 'failed' && snapshot.job.attempts >= snapshot.job.maxAttempts;
  const displayPhase: EnhancementPanelPhase = jobSucceeded ? 'done' : jobFailedTerminal ? 'error' : phase;
  const displayErrorMessage = jobFailedTerminal ? (snapshot?.job.lastError ?? 'errors:code.INTERNAL') : submitError;

  const toastedForJobRef = useRef<string | null>(null);
  useEffect(() => {
    if (jobSucceeded && jobId && toastedForJobRef.current !== jobId) {
      pushToast(assetToast(locale, 'success', t('enhancement:expand.success'), documentTitle));
      toastedForJobRef.current = jobId;
    }
  }, [jobSucceeded, jobId, pushToast, locale, t, documentTitle]);

  function resetForm(): void {
    setPhase('form');
    setJobId(null);
    setSubmitError(null);
  }

  function handleOpenChange(next: boolean): void {
    setOpen(next);
    if (!next) resetForm();
  }

  function handleStart(): void {
    setPhase('starting');
    const instructions = [HOW_MUCH_TEXT[howMuch], direction.trim()].filter(Boolean).join(' ');
    requestTyped(wsClient, 'document.expand', { documentId, versionId, instructions: instructions || undefined })
      .then((accepted: JobAccepted) => {
        setJobId(accepted.jobId);
        setPhase('running');
      })
      .catch((err: unknown) => {
        const parsed = errorAlert.safeParse(err);
        if (parsed.success && parsed.data.code === 'TIER_REQUIRED') {
          setPhase('upsell');
          return;
        }
        setPhase('error');
        setSubmitError(parsed.success ? parsed.data.message : 'errors:code.INTERNAL');
      });
  }

  const creditsLabel = estimate ? t('enhancement:cost.credits', { credits: estimate.credits }) : undefined;

  return (
    <>
      <ListRow
        icon={<Sparkles size={16} aria-hidden="true" />}
        title={t('enhancement:expand.title')}
        trailing={<Badge tone="pro">{t('enhancement:pro.badge')}</Badge>}
        onClick={() => setOpen(true)}
        className={cn(className)}
      />
      <EnhancementPanelShell
        open={open}
        onOpenChange={handleOpenChange}
        title={t('enhancement:expand.title')}
        blurb={t('enhancement:expand.blurb')}
        phase={displayPhase}
        jobId={jobId}
        doneTitle={t('enhancement:expand.doneTitle')}
        errorMessage={displayErrorMessage}
        ctaLabel={t('enhancement:expand.cta')}
        creditsLabel={creditsLabel}
        onStart={handleStart}
        onMaybeLater={() => handleOpenChange(false)}
        onDone={() => handleOpenChange(false)}
        onRetryFromError={resetForm}
      >
        <div className={styles['field']}>
          <span className={styles['fieldLabel']}>{t('enhancement:expand.howMuch')}</span>
          <SegmentedControl
            label={t('enhancement:expand.howMuch')}
            value={howMuch}
            onChange={setHowMuch}
            options={[
              { value: 'few', label: t('enhancement:expand.fewChapters') },
              { value: 'untilTopic', label: t('enhancement:expand.untilTopic') },
            ]}
          />
        </div>
        <Textarea
          label={t('enhancement:expand.direction')}
          placeholder={t('enhancement:expand.directionPlaceholder')}
          value={direction}
          onChange={(event) => setDirection(event.target.value)}
        />
      </EnhancementPanelShell>
    </>
  );
}
